import {
  type Context,
  type PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  createFeatures,
  createPlayerStore,
  type FeatureInstances,
  type FeaturesDefinition,
  PlayerEvents,
  type PlayerStore,
} from '@iconik-io/unified-player-core';

import { PlayerContext, type PlayerContextValue } from './player_context';

function createUsePlayerContext<Value>(context: Context<Value>) {
  return () => {
    const contextValue = useContext(context);
    if (!contextValue) {
      throw new Error(
        'usePlayerContext hook must be used within a PlayerStoreContextProvider component.',
      );
    }

    return contextValue;
  };
}

export function createAppPlayerContext<PlaylistItemMeta>() {
  // biome-ignore lint/plugin: the shared PlayerContext (created with unknown meta) is re-typed per generic instantiation; React offers no way to parameterize an existing context.
  const PlayerContextTyped = PlayerContext as Context<
    PlayerContextValue<PlaylistItemMeta>
  >;

  const PlayerContextProviderTyped = function PlayerContextProviderTyped({
    features = {},
    children,
  }: PropsWithChildren<{
    features?: FeaturesDefinition<PlaylistItemMeta>;
  }>) {
    const storeRef = useRef<PlayerStore<PlaylistItemMeta> | null>(null);
    const featuresRef = useRef<FeatureInstances<PlaylistItemMeta> | null>(null);
    const playerEventsRef = useRef(new PlayerEvents());
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
      // Save the featuresClass promise to be able to destroy the hotkeys feature
      let featuresPromise:
        | Promise<FeatureInstances<PlaylistItemMeta>>
        | undefined;

      const init = async () => {
        storeRef.current = createPlayerStore<PlaylistItemMeta>(
          playerEventsRef.current,
        );
        featuresPromise = createFeatures(
          storeRef.current,
          playerEventsRef.current,
          features,
        );
        featuresRef.current = await featuresPromise;
        setIsReady(true);
      };

      init();

      return () => {
        featuresPromise?.then((featuresClass) => {
          featuresClass.hotkeys?.destroy();
        });
      };
    }, [features]);

    if (!storeRef.current || !featuresRef.current || !isReady) {
      return null;
    }

    return (
      <PlayerContextTyped.Provider
        value={{
          store: storeRef.current,
          features: featuresRef.current,
          playerEvents: playerEventsRef.current,
        }}
      >
        {children}
      </PlayerContextTyped.Provider>
    );
  };

  return {
    PlayerContextProvider: PlayerContextProviderTyped,
    usePlayerContext: createUsePlayerContext(PlayerContextTyped),
  };
}
